<template>
    <div class="content-ellipse-card">
        <ContentEllipsesCardTitle
            v-model:is-open="isOpen"
            :title="title"
            :question-text="questionText"
            :has-more-content="hasMoreContent"
        />
        <div
            ref="blockContentWrapRef"
            class="content-ellipse-card__content"
        >
            <div
                ref="blockContentRef"
                :class="isOpen ? 'is--more' : 'is--less'"
                class="block-content"
            >
                <AnswerRecursiveContent
                    :content="content"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import { Question } from '@/types/api'
import ContentEllipsesCardTitle from '@/components/Card/ContentEllipse/ContentEllipsesCardTitle.vue'
import AnswerRecursiveContent from '@/components/Common/AnswerRecursiveContent.vue'

const { question } = defineProps<{
    question: Question
}>()

const blockContentWrapRef = ref<HTMLElement>()
const blockContentRef = ref<HTMLElement>()

const hasMoreContent = ref<boolean>(false)
const isOpen = ref<boolean>(true)

const title = computed(() => {
    return question.key.replaceAll('_', ' ')
})
const content = computed(() => {
    return safeJSONParse(question.answer)
})

const isContentArray = computed(() => {
    return Array.isArray(content.value)
})
const questionText = computed(() => {
    if ('value' in question && question.value) {
        return question.value
    } else if ('question' in question && question.question) {
        return question.question
    } else {
        return null
    }
})
// const isDisableExpand = computed(() => {
//     return !isContentString.value
// })

/* Methods */
function init () {
    nextTick(() => {
        if (!blockContentRef.value || !blockContentWrapRef.value) {
            return
        }
        const { height = 0 } = blockContentWrapRef.value.getBoundingClientRect()

        hasMoreContent.value = height > 48 || isContentArray.value && content.value.length > 2
        isOpen.value = false
    })
}

function safeJSONParse (jsonString: string | number | boolean) {
    if (typeof jsonString !== 'string') {
        return jsonString
    }
    if (!jsonString) {
        return ''
    }
    try {
        if (!jsonString.startsWith('[') || !jsonString.startsWith('{')) {
            return JSON.parse(jsonString)
        }
    } catch (e) {
        return jsonString
    }
}

onMounted(() => {
    init()
})

defineExpose({
    expand: () => isOpen.value = true,
    collapse: () => isOpen.value = false
})
</script>

<style lang="scss">
.content-ellipse-card {
    @apply w-full border border-ui-lines rounded-lg p-2 md:p-4;

    &__content-title {
        @apply w-full flex justify-between items-center gap-4;
    }

    .block-title {
        @apply text-base font-semibold pb-3;
    }

    &__content {
        width: calc(100% - 64px);
    }

    .block-help-action {
        @apply text-placeholders;
    }

    .block-content {
        @apply text-sm;
        transition: var(--base-transition);
        line-height: 20px;
    }

    .block-content.is--less {
        width: max-content;
        max-width: 100%;
        word-wrap: break-word;
        //white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        max-height: 50px;
    }

    .block-content.is--more {
        white-space: normal;
        height: auto;
    }

    &__arrow span {
        @apply flex justify-center items-center;
    }
}
</style>
